import { useSyncExternalStore } from 'react'

/**
 * A FRIEND'S AGENT ON THE STAGE — what the two halves of its screen tell each other.
 *
 * It opens like an agent of your own (owner, 2026-10-17: 朋友 Agent 的 session ui 可以用右開的方式。
 * 完全參照目前一般 Agent 的 UI): the stage draws the conversation (SharedAgentPanel) and App's own
 * header and composer sit around it. They meet here, keyed by share — never by session name, which
 * a friend's agent may share with one of the reader's own (the live and echo stores of an own agent
 * are keyed by name, and must never draw into this one):
 *
 *   echo  what the reader just sent, drawn until the conversation shows it (the composer adds,
 *         the panel clears what has landed)
 *   live  working / waiting for an answer / over — the panel learns it, the composer's Stop and
 *         the pill read it
 *   kick  the composer sent something: the panel reads again now rather than at the next poke
 */
export type SharedLive = {
  /** The agent is in a turn (the cloud's status for this one agent). */
  working: boolean
  /** Stopped at a question only its owner can answer on their machine. */
  asking: boolean
  /** Why the share is over, in the cloud's words — null while it is not. */
  gone: string | null
}

const IDLE: SharedLive = { working: false, asking: false, gone: null }
const echoes = new Map<string, string[]>()
const lives = new Map<string, SharedLive>()
const kicks = new Map<string, Set<() => void>>()
const subs = new Set<() => void>()
let version = 0

const changed = () => {
  version += 1
  for (const fn of subs) fn()
}
const subscribe = (fn: () => void) => {
  subs.add(fn)
  return () => {
    subs.delete(fn)
  }
}
const snapshot = () => version

const NONE: string[] = []

export function addSharedEcho(id: string, text: string): void {
  echoes.set(id, [...(echoes.get(id) ?? []), text])
  changed()
}

/** Drop every echo the conversation now shows (`landed` decides). */
export function clearSharedEchoes(id: string, landed: (text: string) => boolean): void {
  const before = echoes.get(id)
  if (!before?.length) return
  const after = before.filter((t) => !landed(t))
  if (after.length === before.length) return
  if (after.length) echoes.set(id, after)
  else echoes.delete(id)
  changed()
}

export function useSharedEchoes(id: string): string[] {
  useSyncExternalStore(subscribe, snapshot, snapshot)
  return echoes.get(id) ?? NONE
}

export function setSharedLive(id: string, next: SharedLive): void {
  const was = lives.get(id) ?? IDLE
  if (was.working === next.working && was.asking === next.asking && was.gone === next.gone) return
  lives.set(id, next)
  changed()
}

export function useSharedLive(id: string | null): SharedLive {
  useSyncExternalStore(subscribe, snapshot, snapshot)
  return (id && lives.get(id)) || IDLE
}

/** The composer sent something: whoever draws this share reads again. */
export function kickShared(id: string): void {
  for (const fn of kicks.get(id) ?? []) fn()
}

export function onSharedKick(id: string, fn: () => void): () => void {
  const set = kicks.get(id) ?? new Set()
  set.add(fn)
  kicks.set(id, set)
  return () => {
    set.delete(fn)
    if (!set.size) kicks.delete(id)
  }
}

/** Test seam. */
export function _resetSharedSession(): void {
  echoes.clear()
  lives.clear()
  kicks.clear()
  changed()
}
